import React from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizedDateInput, Spinner } from '../../../shared/components';
import { getContactChannelTypeLabel, maskChannelIdentitiesInText } from '../../contacts/contactChannelUtils';
import ConversationChannelBadge from './ConversationChannelBadge';
import { getConversationRowChannelIcons, getConversationChannelIconTitles, resolveExternalConversationAvatarIcon } from '../conversationChannelIcons';
import { Conversation, ConversationCustomList, ConversationParticipantOption, Message } from '../types/Conversation';
import { getConversationKind, isDirectConversation, isExternalConversation } from '../utils/conversationClassification';
import { formatMediaListPreview, getConversationLastActivityIso } from '../utils/messageUtils';
import { getInstagramCommentPreviewPrefix, isInstagramCommentMessage, resolveConversationHasInstagramComments, filterMessagesForCommentVisibility } from '../utils/instagramComposerUtils';
import { getMessengerCommentPreviewPrefix, isMessengerCommentMessage, resolveConversationHasMessengerComments } from '../utils/messengerComposerUtils';
import pageStyles from './ConversationsPage.module.css';
import { NavSection, DraftsSentTab, FilterStatus, FilterAiMuted, FilterChannel, buildCustomListNavSection, getCustomListIdFromNavSection, isCustomListNavSection } from '../types/conversationNav';
import { formatDateTime, formatConversationListTimestamp } from '../../../shared/utils/localization';
import { formatChannelAccountOptionLabel } from '../../connectors/types/channelAccount';

interface GlobalSearchResult {
  conversationId: string;
  conversationTitle: string;
  message: Message;
}

interface ScheduledNavItem {
  id: string;
  conversationId: string;
  conversationTitle: string;
  content: string;
  messageType: string;
  fileName?: string;
  scheduledAtUtc: string;
}

interface SentNavItem {
  id: string;
  conversationId: string;
  conversationTitle: string;
  content: string;
  messageType: string;
  fileName?: string;
  timestamp: string;
}

interface ConversationListPaneProps {
  isListExpanded: boolean;
  isDetailsMaximized: boolean;
  isMobileLayout: boolean;
  navSection: NavSection;
  setNavSection: (value: NavSection) => void;
  conversationCount: number;
  directConversationCount: number;
  channelConversationCount: number;
  externalConversationCount: number;
  hiddenConversationCount: number;
  draftsSentCount: number;
  draftsSentTab: DraftsSentTab;
  setDraftsSentTab: (value: DraftsSentTab) => void;
  creating: boolean;
  openCreatePanel: () => void;
  showCreatePanel: boolean;
  createConversationType: 'direct-message' | 'channel' | 'external-chat' | null;
  onSetConversationType: (type: 'direct-message' | 'channel' | 'external-chat' | null) => void | Promise<void>;
  createTitle: string;
  setCreateTitle: (value: string) => void;
  createContactSearch: string;
  setCreateContactSearch: (value: string) => void;
  loadingParticipantOptions: boolean;
  filteredParticipantOptions: ConversationParticipantOption[];
  selectedParticipantId: string | null;
  selectedParticipantIds: Set<string>;
  toggleParticipantSelection: (participantId: string) => void;
  toggleChannelParticipantSelection: (participantId: string) => void;
  participantOptions: ConversationParticipantOption[];
  onCancelCreatePanel: () => void;
  onCreate: () => void;
  channelAccountsForCreate: Array<{
    id: string;
    displayName: string;
    externalAccountId: string;
    isDefault: boolean;
    channel?: 'WhatsApp' | 'Instagram' | 'Webchat' | 'Telegram' | 'Messenger' | 'TikTok' | 'Voice' | 'Sms';
    connectionKind?: 'cloud' | 'app' | null;
  }>;
  loadingChannelAccountsForCreate: boolean;
  selectedChannelAccountId: string | null;
  setSelectedChannelAccountId: (value: string | null) => void;
  requiresChannelAccountSelection: boolean;
  filterStatus: FilterStatus;
  setFilterStatus: (value: FilterStatus) => void;
  filterAiMuted: FilterAiMuted;
  setFilterAiMuted: (value: FilterAiMuted) => void;
  filterChannel: FilterChannel;
  setFilterChannel: (value: FilterChannel) => void;
  filterDate: string;
  setFilterDate: (value: string) => void;
  showCommentThreads: boolean;
  setShowCommentThreads: React.Dispatch<React.SetStateAction<boolean>>;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  search: string;
  setSearch: (value: string) => void;
  globalSearchResults: GlobalSearchResult[];
  showSearchPanel: boolean;
  setShowSearchPanel: (value: boolean) => void;
  onSelectSearchResult: (result: GlobalSearchResult) => void;
  loadingList: boolean;
  loadingMoreList: boolean;
  hasMoreConversations: boolean;
  onLoadMoreConversations: () => void;
  loadingScheduledNav: boolean;
  listError: string | null;
  orderedFilteredList: Conversation[];
  unreadCountByConversationId: Record<string, number>;
  scheduledNavItems: ScheduledNavItem[];
  sentNavItems: SentNavItem[];
  selectedId: string | null;
  onSelectConversation: (conversationId: string) => void;
  onSelectScheduledNavItem: (conversationId: string) => void;
  onSelectSentNavItem: (conversationId: string, messageId: string) => void;
  getConversationDisplayName: (conversation: Conversation) => string;
  currentEmployeeId?: string;
  onOpenFloatingChat: (conversationId: string) => void;
  openRowMenuId: string | null;
  setOpenRowMenuId: (value: string | null | ((prev: string | null) => string | null)) => void;
  togglePinnedConversation: (conversationId: string, currentPinned: boolean) => void;
  toggleFavoriteConversation: (conversationId: string, currentFavorite: boolean) => void;
  hideConversationForMe: (conversationId: string) => void;
  restoreConversationForMe: (conversationId: string) => void;
  onOpenConversationNotes: (conversationId: string) => void;
  employeeOptions: Array<{ id: string; name: string }>;
  onAssignConversation: (conversationId: string, employeeId: string) => void;
  onUnassignConversation: (conversationId: string) => void;
  customLists: ConversationCustomList[];
  onCreateCustomList: () => void;
  onUpdateCustomList: (listId: string, name: string) => Promise<boolean>;
  onDeleteCustomList: (listId: string) => Promise<boolean>;
  onMoveConversationToCustomList: (conversationId: string, customListId: string | null) => void;
  maskChannelValues?: boolean;
}

const PHONE_LIKE_WORD = /^[+(\d][\d\s()+.-]*$/;

/**
 * Contact titles frequently carry the phone number alongside the name ("Shahmar +99450..."), which
 * turns naive initials into "S+". Numeric words are skipped, and a title made up entirely of them
 * yields null so the caller can fall back to something more useful than a stray digit.
 */
const resolveNameInitials = (value: string): string | null => {
  const words = value
    .trim()
    .split(/\s+/)
    .filter(word => Boolean(word) && !PHONE_LIKE_WORD.test(word))
    .slice(0, 2);

  if (words.length === 0) {
    return null;
  }

  return words.map(word => word.charAt(0).toLocaleUpperCase()).join('');
};

const ConversationListPane: React.FC<ConversationListPaneProps> = ({
  isListExpanded,
  isDetailsMaximized,
  isMobileLayout,
  navSection,
  setNavSection,
  conversationCount,
  directConversationCount,
  channelConversationCount,
  externalConversationCount,
  hiddenConversationCount,
  draftsSentCount,
  draftsSentTab,
  setDraftsSentTab,
  creating,
  openCreatePanel,
  showCreatePanel,
  createConversationType,
  onSetConversationType,
  createTitle,
  setCreateTitle,
  createContactSearch,
  setCreateContactSearch,
  loadingParticipantOptions,
  filteredParticipantOptions,
  selectedParticipantId,
  selectedParticipantIds,
  toggleParticipantSelection,
  toggleChannelParticipantSelection,
  participantOptions,
  onCancelCreatePanel,
  onCreate,
  channelAccountsForCreate,
  loadingChannelAccountsForCreate,
  selectedChannelAccountId,
  setSelectedChannelAccountId,
  requiresChannelAccountSelection,
  filterStatus,
  setFilterStatus,
  filterAiMuted,
  setFilterAiMuted,
  filterChannel,
  setFilterChannel,
  filterDate,
  setFilterDate,
  showCommentThreads,
  setShowCommentThreads,
  searchInputRef,
  search,
  setSearch,
  globalSearchResults,
  showSearchPanel,
  setShowSearchPanel,
  onSelectSearchResult,
  loadingList,
  loadingMoreList,
  hasMoreConversations,
  onLoadMoreConversations,
  loadingScheduledNav,
  listError,
  orderedFilteredList,
  unreadCountByConversationId,
  scheduledNavItems,
  sentNavItems,
  selectedId,
  onSelectConversation,
  onSelectScheduledNavItem,
  onSelectSentNavItem,
  getConversationDisplayName,
  currentEmployeeId,
  onOpenFloatingChat,
  openRowMenuId,
  setOpenRowMenuId,
  togglePinnedConversation,
  toggleFavoriteConversation,
  hideConversationForMe,
  restoreConversationForMe,
  onOpenConversationNotes,
  employeeOptions,
  onAssignConversation,
  onUnassignConversation,
  customLists,
  onCreateCustomList,
  onUpdateCustomList,
  onDeleteCustomList,
  onMoveConversationToCustomList,
  maskChannelValues = false,
}) => {
  const { t } = useTranslation(['conversations', 'common']);
  const createTitleInputRef = React.useRef<HTMLInputElement | null>(null);
  const createParticipantSearchInputRef = React.useRef<HTMLInputElement | null>(null);
  const filterMenuRef = React.useRef<HTMLDivElement | null>(null);
  const rowMenuWrapRef = React.useRef<HTMLDivElement | null>(null);
  const listScrollRef = React.useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = React.useRef<HTMLDivElement | null>(null);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = React.useState(false);
  const [assignEmployeeSearch, setAssignEmployeeSearch] = React.useState('');
  const [editingCustomListId, setEditingCustomListId] = React.useState<string | null>(null);
  const [editCustomListName, setEditCustomListName] = React.useState('');
  const [customListMutationBusy, setCustomListMutationBusy] = React.useState(false);

  React.useEffect(() => {
    setAssignEmployeeSearch('');
  }, [openRowMenuId]);

  React.useEffect(() => {
    if (!openRowMenuId) {
      rowMenuWrapRef.current = null;
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!rowMenuWrapRef.current?.contains(event.target as Node)) {
        setOpenRowMenuId(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openRowMenuId, setOpenRowMenuId]);

  const participantOptionByKey = React.useMemo(() => {
    return new Map(participantOptions.map(option => [`${option.type}:${option.id}`, option]));
  }, [participantOptions]);

  const activeCustomListId = getCustomListIdFromNavSection(navSection);
  const activeCustomList = activeCustomListId
    ? customLists.find(list => list.id === activeCustomListId)
    : undefined;

  const statusFilterOptions = React.useMemo<Array<{ value: FilterStatus; label: string }>>(() => [
    { value: 'all', label: t('all', { ns: 'common' }) },
    { value: 'signed', label: t('signed', { ns: 'conversations' }) },
    { value: 'unsigned', label: t('unsigned', { ns: 'conversations' }) },
  ], [t]);

  const aiFilterOptions = React.useMemo<Array<{ value: FilterAiMuted; label: string }>>(() => [
    { value: 'all', label: t('aiAll', { ns: 'conversations' }) },
    { value: 'muted', label: t('aiMutedOption', { ns: 'conversations' }) },
    { value: 'unmuted', label: t('aiUnmutedOption', { ns: 'conversations' }) },
  ], [t]);

  const channelFilterOptions = React.useMemo<Array<{ value: FilterChannel; label: string }>>(() => [
    { value: 'all', label: t('all', { ns: 'common' }) },
    { value: 'whatsApp', label: t('whatsAppChannel', { ns: 'conversations' }) },
    { value: 'telegram', label: t('telegramChannel', { ns: 'conversations' }) },
    { value: 'instagram', label: t('instagramChannel', { ns: 'conversations' }) },
    { value: 'messenger', label: t('messengerChannel', { ns: 'conversations' }) },
    { value: 'webchat', label: t('webchatChannel', { ns: 'conversations' }) },
  ], [t]);

  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (activeCustomListId) {
      count += 1;
    }
    if (navSection !== 'channels' && filterStatus !== 'all') {
      count += 1;
    }
    if (navSection !== 'channels' && filterAiMuted !== 'all') {
      count += 1;
    }
    if (filterChannel !== 'all') {
      count += 1;
    }
    if (filterDate) {
      count += 1;
    }
    return count;
  }, [activeCustomListId, filterAiMuted, filterChannel, filterDate, filterStatus, navSection]);

  const handleSelectInboxView = React.useCallback(() => {
    if (isCustomListNavSection(navSection)) {
      setNavSection('conversations');
    }
  }, [navSection, setNavSection]);

  const handleSelectCustomList = React.useCallback((listId: string) => {
    setNavSection(buildCustomListNavSection(listId));
    setIsFilterMenuOpen(false);
  }, [setNavSection]);

  const handleCreateCustomListFromFilter = React.useCallback(() => {
    setEditingCustomListId(null);
    setEditCustomListName('');
    setIsFilterMenuOpen(false);
    onCreateCustomList();
  }, [onCreateCustomList]);

  const startEditCustomList = React.useCallback((list: ConversationCustomList) => {
    setEditingCustomListId(list.id);
    setEditCustomListName(list.name);
  }, []);

  const cancelEditCustomList = React.useCallback(() => {
    setEditingCustomListId(null);
    setEditCustomListName('');
  }, []);

  const handleSaveCustomListEdit = React.useCallback(async () => {
    if (!editingCustomListId) {
      return;
    }

    const name = editCustomListName.trim();
    if (!name) {
      return;
    }

    setCustomListMutationBusy(true);
    try {
      const saved = await onUpdateCustomList(editingCustomListId, name);
      if (saved) {
        cancelEditCustomList();
      }
    } finally {
      setCustomListMutationBusy(false);
    }
  }, [cancelEditCustomList, editCustomListName, editingCustomListId, onUpdateCustomList]);

  const handleDeleteCustomList = React.useCallback(async () => {
    if (!editingCustomListId) {
      return;
    }

    const list = customLists.find(item => item.id === editingCustomListId);
    const confirmed = window.confirm(
      t('deleteCustomListConfirm', { name: list?.name ?? '', ns: 'conversations' }),
    );
    if (!confirmed) {
      return;
    }

    setCustomListMutationBusy(true);
    try {
      const deleted = await onDeleteCustomList(editingCustomListId);
      if (deleted) {
        cancelEditCustomList();
      }
    } finally {
      setCustomListMutationBusy(false);
    }
  }, [cancelEditCustomList, customLists, editingCustomListId, onDeleteCustomList, t]);

  const getDirectConversationParticipant = React.useCallback((conversation: Conversation) => {
    if (!isDirectConversation(conversation)) {
      return null;
    }

    const nonAiParticipants = conversation.participants.filter(participant => participant.type !== 'aiAssistant');
    if (nonAiParticipants.length !== 2) {
      return null;
    }

    const externalParticipant = nonAiParticipants.find(participant => participant.type === 'customer' || participant.type === 'lead');
    if (externalParticipant) {
      return externalParticipant;
    }

    if (currentEmployeeId) {
      const otherCompanyMember = nonAiParticipants.find(
        participant => !(participant.type === 'companyMember' && participant.id === currentEmployeeId),
      );

      if (otherCompanyMember) {
        return otherCompanyMember;
      }
    }

    return nonAiParticipants[0] ?? null;
  }, [currentEmployeeId]);

  const getParticipantMeta = (option: ConversationParticipantOption) => {
    const metaParts: string[] = [];

    if (option.type === 'companyMember') {
      metaParts.push(t('employee', { ns: 'conversations' }));
    } else if (option.type === 'customer') {
      metaParts.push(t('contact', { ns: 'conversations' }));
    } else if (option.type === 'lead') {
      metaParts.push(t('lead', { ns: 'conversations' }));
    }

    if (option.contactChannelType && option.contactChannelValue) {
      const channelLabel = getContactChannelTypeLabel(option.contactChannelType);
      const valueLabel = option.contactChannelLabel ? `${channelLabel} (${option.contactChannelLabel})` : channelLabel;
      metaParts.push(`${t('channel', { ns: 'conversations' })}: ${valueLabel} ${maskChannelValues ? maskChannelIdentitiesInText(option.contactChannelValue) : option.contactChannelValue}`);
    }

    if (option.email) {
      metaParts.push(option.email);
    }

    return metaParts.join(' • ');
  };

  const getParticipantDisplayTitle = (option: ConversationParticipantOption) => {
    if (option.type === 'aiAssistant') {
      return t('aiAssistant', { ns: 'conversations' });
    }

    const firstName = option.firstName?.trim();
    const lastName = option.lastName?.trim();
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const displayName = fullName || option.name;
    return maskChannelValues ? maskChannelIdentitiesInText(displayName) : displayName;
  };

  const displayConversationTitle = (title: string) => {
    return maskChannelValues ? maskChannelIdentitiesInText(title) : title;
  };

  const getConversationPreview = (conversation: Conversation) => {
    const previewMessages = filterMessagesForCommentVisibility(conversation.messages, showCommentThreads);

    if (previewMessages.length === 0) {
      return t('noMessagesYet', { ns: 'conversations' });
    }

    const lastMessage = [...previewMessages].sort((left, right) => {
      return new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime();
    })[0];

    const content = (lastMessage.content || '').trim();
    if (lastMessage.messageType === 'system') {
      return content || t('systemUpdate', { ns: 'conversations' });
    }

    const formatted = formatMediaListPreview(
      lastMessage.content,
      lastMessage.messageType,
      lastMessage.fileName,
      key => t(key, { ns: 'conversations' }),
    );

    const commentPrefix = isInstagramCommentMessage(lastMessage)
      ? getInstagramCommentPreviewPrefix(lastMessage, key => t(key, { ns: 'conversations' }))
      : isMessengerCommentMessage(lastMessage)
        ? getMessengerCommentPreviewPrefix(lastMessage, key => t(key, { ns: 'conversations' }))
        : null;
    if (commentPrefix && content.length > 0 && formatted.includes(content)) {
      return `${commentPrefix} ${formatted}`;
    }

    return formatted;
  };

  const formatNavMessagePreview = (content: string, messageType: string, fileName?: string) => {
    return formatMediaListPreview(content, messageType, fileName, key => t(key, { ns: 'conversations' }));
  };

  const isConvSection = navSection !== 'drafts-sent';
  const canCreateConversation = navSection !== 'drafts-sent' && navSection !== 'spam' && !activeCustomListId;

  React.useEffect(() => {
    if (!isConvSection || loadingList || !hasMoreConversations) {
      return;
    }

    const root = listScrollRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        onLoadMoreConversations();
      }
    }, {
      root,
      rootMargin: '160px 0px',
      threshold: 0,
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    hasMoreConversations,
    isConvSection,
    loadingList,
    loadingMoreList,
    onLoadMoreConversations,
    orderedFilteredList.length,
  ]);

  const isExternalCreateEntry = navSection === 'external';
  const headerTitle = activeCustomList
    ? activeCustomList.name
    : navSection === 'direct-messages'
    ? t('directMessagesHeader', { ns: 'conversations' })
    : navSection === 'channels'
      ? t('channelsHeader', { ns: 'conversations' })
      : navSection === 'external'
        ? t('externalConversationsHeader', { ns: 'conversations' })
        : navSection === 'spam'
          ? t('spamConversationsHeader', { ns: 'conversations' })
          : t('conversationsHeader', { ns: 'conversations' });
  const createModeHelperText = createConversationType === 'direct-message'
    ? t('directMessageModeHelper', { ns: 'conversations' })
    : createConversationType === 'channel'
      ? t('channelModeHelper', { ns: 'conversations' })
      : createConversationType === 'external-chat'
        ? t('externalChatModeHelper', { ns: 'conversations' })
        : '';

  React.useEffect(() => {
    if (!showCreatePanel || createConversationType === null) {
      return;
    }

    const targetInput = createConversationType === 'channel'
      ? createTitleInputRef.current
      : createParticipantSearchInputRef.current;

    if (!targetInput) {
      return;
    }

    const focusHandle = window.setTimeout(() => {
      targetInput.focus();
      targetInput.select?.();
    }, 0);

    return () => window.clearTimeout(focusHandle);
  }, [createConversationType, showCreatePanel]);

  React.useEffect(() => {
    if (!isFilterMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!filterMenuRef.current?.contains(event.target as Node)) {
        setIsFilterMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isFilterMenuOpen]);

  const handleStatusCheckboxChange = React.useCallback((value: FilterStatus) => {
    setFilterStatus(value);
  }, [setFilterStatus]);

  const handleAiCheckboxChange = React.useCallback((value: FilterAiMuted) => {
    setFilterAiMuted(value);
  }, [setFilterAiMuted]);

  const handleChannelCheckboxChange = React.useCallback((value: FilterChannel) => {
    setFilterChannel(value);
  }, [setFilterChannel]);

  const handleClearFilters = React.useCallback(() => {
    setFilterStatus('all');
    setFilterAiMuted('all');
    setFilterChannel('all');
    setFilterDate('');
    if (isCustomListNavSection(navSection)) {
      setNavSection('conversations');
    }
  }, [navSection, setFilterAiMuted, setFilterChannel, setFilterDate, setFilterStatus, setNavSection]);

  return (
    <div
      className={[
        pageStyles.listPane,
        isListExpanded || (isMobileLayout && !selectedId) ? pageStyles.expanded : '',
        isMobileLayout && selectedId ? pageStyles.mobileHidden : '',
        isDetailsMaximized ? pageStyles.mobileHidden : '',
      ].filter(Boolean).join(' ')}
    >
      <div className={pageStyles.listHeader}>
        <span>{headerTitle}</span>
        <div className={pageStyles.listHeaderActions}>
          {canCreateConversation && (
            <button
              className={`${pageStyles.newBtn} ${isExternalCreateEntry ? pageStyles.newBtnExternal : ''}`}
              onClick={openCreatePanel}
              disabled={creating}
            >
              {creating ? t('creating', { ns: 'conversations' }) : isExternalCreateEntry ? (
                <span className={pageStyles.newBtnContent}>
                  <span className={pageStyles.newBtnIcon} aria-hidden="true">↗</span>
                  <span>{t('externalChat', { ns: 'conversations' })}</span>
                </span>
              ) : t('newConversation', { ns: 'conversations' })}
            </button>
          )}
        </div>
      </div>
      <div className={pageStyles.subCategoryBar}>
        <div className={pageStyles.primaryTabs} role="tablist" aria-label={t('conversationsHeader', { ns: 'conversations' })}>
          <button
            type="button"
            className={`${pageStyles.subCategoryBtn} ${pageStyles.tabVariantAll} ${navSection === 'conversations' ? pageStyles.subCategoryBtnActive : ''}`}
            onClick={() => setNavSection('conversations')}
            title={t('conversationsHeader', { ns: 'conversations' })}
          >
            <span className={pageStyles.tabLabelGroup}>
              <span className={pageStyles.tabIcon} aria-hidden="true">
                <svg viewBox="0 0 20 20" className={pageStyles.tabIconSvg}>
                  <path d="M4.75 5.25h10.5M4.75 10h10.5M4.75 14.75h7.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              <span className={pageStyles.tabLabel}>{t('conversationsHeader', { ns: 'conversations' })}</span>
              <span className={pageStyles.tabBadge}>{t('allTab', { ns: 'conversations' })}</span>
            </span>
          </button>
          <button
            type="button"
            className={`${pageStyles.subCategoryBtn} ${pageStyles.tabVariantDirect} ${navSection === 'direct-messages' ? pageStyles.subCategoryBtnActive : ''}`}
            onClick={() => setNavSection('direct-messages')}
            title={t('directMessagesHeader', { ns: 'conversations' })}
          >
            <span className={pageStyles.tabLabelGroup}>
              <span className={pageStyles.tabIcon} aria-hidden="true">
                <svg viewBox="0 0 20 20" className={pageStyles.tabIconSvg}>
                  <circle cx="10" cy="7" r="3" fill="none" stroke="currentColor" strokeWidth="1.7" />
                  <path d="M4.75 15.25c1.2-2.45 3.02-3.67 5.25-3.67 2.23 0 4.05 1.22 5.25 3.67" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              </span>
              <span className={pageStyles.tabLabel}>{t('directMessagesHeader', { ns: 'conversations' })}</span>
              <span className={pageStyles.tabBadge}>{t('directTabShort', { ns: 'conversations' })}</span>
            </span>
          </button>
          <button
            type="button"
            className={`${pageStyles.subCategoryBtn} ${pageStyles.tabVariantChannel} ${navSection === 'channels' ? pageStyles.subCategoryBtnActive : ''}`}
            onClick={() => setNavSection('channels')}
            title={t('channelsHeader', { ns: 'conversations' })}
          >
            <span className={pageStyles.tabLabelGroup}>
              <span className={pageStyles.tabIcon} aria-hidden="true">
                <svg viewBox="0 0 20 20" className={pageStyles.tabIconSvg}>
                  <path d="M7 4.5 5.75 15.5M13.25 4.5 12 15.5M4.5 7h11M3.75 13h11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
              <span className={pageStyles.tabLabel}>{t('channelsHeader', { ns: 'conversations' })}</span>
              <span className={pageStyles.tabBadge}>{t('channelsTabShort', { ns: 'conversations' })}</span>
            </span>
          </button>
          <button
            type="button"
            className={`${pageStyles.subCategoryBtn} ${pageStyles.tabVariantExternal} ${navSection === 'external' ? pageStyles.subCategoryBtnActive : ''}`}
            onClick={() => setNavSection('external')}
            title={t('externalConversationsHeader', { ns: 'conversations' })}
          >
            <span className={pageStyles.tabLabelGroup}>
              <span className={pageStyles.tabIcon} aria-hidden="true">
                <svg viewBox="0 0 20 20" className={pageStyles.tabIconSvg}>
                  <path d="M14.5 5.5L8 12m6.25-6.75v5.5M14.5 5.5h-5.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M14.5 4.75h2v11.5c0 .414-.336.75-.75.75H4.75c-.414 0-.75-.336-.75-.75v-11.5h2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span className={pageStyles.tabLabel}>{t('externalConversationsHeader', { ns: 'conversations' })}</span>
              <span className={pageStyles.tabBadge}>{t('externalTabShort', { ns: 'conversations' })}</span>
            </span>
          </button>
        </div>
        <div className={pageStyles.secondaryTabs} role="tablist" aria-label={t('conversationSecondaryNav', { ns: 'conversations' })}>
          <button
            type="button"
            className={`${pageStyles.secondaryTabBtn} ${navSection === 'drafts-sent' ? pageStyles.secondaryTabBtnActive : ''}`}
            onClick={() => setNavSection('drafts-sent')}
          >
            <span className={pageStyles.secondaryTabLabel}>{t('draftsAndSentHeader', { ns: 'conversations' })}</span>
          </button>
          <button
            type="button"
            className={`${pageStyles.secondaryTabBtn} ${navSection === 'spam' ? pageStyles.secondaryTabBtnActive : ''}`}
            onClick={() => setNavSection('spam')}
            title={t('spamTab', { ns: 'conversations' })}
          >
            <svg viewBox="0 0 20 20" className={pageStyles.secondaryTabIcon} aria-hidden="true">
              <path d="M4.75 6.5c1.6-1.7 3.37-2.55 5.25-2.55 3.36 0 6.35 2.2 8.25 6.05-1.9 3.85-4.89 6.05-8.25 6.05-3.36 0-6.35-2.2-8.25-6.05.65-1.32 1.4-2.4 2.25-3.25Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="10" cy="10" r="2.15" fill="none" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className={pageStyles.secondaryTabLabel}>{t('spamTab', { ns: 'conversations' })}</span>
          </button>
        </div>
      </div>
      <div className={`${pageStyles.draftsTabs} ${navSection !== 'drafts-sent' ? pageStyles.draftsTabsHidden : ''}`} aria-hidden={navSection !== 'drafts-sent'}>
        <button
          type="button"
          className={`${pageStyles.draftsTabBtn} ${draftsSentTab === 'scheduled' ? pageStyles.draftsTabBtnActive : ''}`}
          onClick={() => setDraftsSentTab('scheduled')}
          tabIndex={navSection !== 'drafts-sent' ? -1 : undefined}
        >
          <span>{t('draftsScheduledTab', { ns: 'conversations' })}</span>
          {scheduledNavItems.length > 0 ? (
            <span className={pageStyles.tabBadge}>{scheduledNavItems.length}</span>
          ) : null}
        </button>
        <button
          type="button"
          className={`${pageStyles.draftsTabBtn} ${draftsSentTab === 'sent' ? pageStyles.draftsTabBtnActive : ''}`}
          onClick={() => setDraftsSentTab('sent')}
          tabIndex={navSection !== 'drafts-sent' ? -1 : undefined}
        >
          <span>{t('draftsSentTab', { ns: 'conversations' })}</span>
        </button>
      </div>
      {isConvSection && showCreatePanel && createConversationType !== null && (
        <div className={pageStyles.createPanel}>
          <div className={pageStyles.createPanelHeader}>
            <button
              className={pageStyles.createPanelBackBtn}
              onClick={onCancelCreatePanel}
              title={t('cancel', { ns: 'conversations' })}
            >
              ←
            </button>
            <span className={pageStyles.createPanelTitle}>
              {createConversationType === 'direct-message'
                ? t('newDirectMessage', { ns: 'conversations' })
                : createConversationType === 'channel'
                  ? t('newChannel', { ns: 'conversations' })
                  : t('newExternalChat', { ns: 'conversations' })}
            </span>
          </div>
            <div className={pageStyles.createModeSwitch}>
              <button
                type="button"
                className={`${pageStyles.createModeButton} ${createConversationType === 'direct-message' ? pageStyles.createModeButtonActive : ''}`}
                onClick={() => onSetConversationType('direct-message')}
              >
                {t('message', { ns: 'conversations' })}
              </button>
              <button
                type="button"
                className={`${pageStyles.createModeButton} ${createConversationType === 'channel' ? pageStyles.createModeButtonActive : ''}`}
                onClick={() => onSetConversationType('channel')}
              >
                {t('channel', { ns: 'conversations' })}
              </button>
              <button
                type="button"
                className={`${pageStyles.createModeButton} ${createConversationType === 'external-chat' ? pageStyles.createModeButtonActive : ''}`}
                onClick={() => onSetConversationType('external-chat')}
              >
                {t('externalChat', { ns: 'conversations' })}
              </button>
            </div>
            <div className={pageStyles.createModeHelper}>{createModeHelperText}</div>
          {createConversationType === 'channel' && (
            <input
              ref={createTitleInputRef}
              className={pageStyles.createTitleInput}
              placeholder={t('channelNameOptional', { ns: 'conversations' })}
              value={createTitle}
              onChange={event => setCreateTitle(event.target.value)}
            />
          )}
          <input
            ref={createParticipantSearchInputRef}
            className={pageStyles.createTitleInput}
            placeholder={createConversationType === 'external-chat'
              ? t('searchExternalRecipients', { ns: 'conversations' })
              : t('searchParticipantsByNameEmail', { ns: 'conversations' })}
            value={createContactSearch}
            onChange={event => setCreateContactSearch(event.target.value)}
          />
          <div className={pageStyles.createContactsLabel}>
            {createConversationType === 'channel'
              ? t('selectParticipants', { ns: 'conversations' })
              : createConversationType === 'external-chat'
                ? t('selectExternalRecipient', { ns: 'conversations' })
                : t('selectParticipant', { ns: 'conversations' })}
          </div>
          {loadingParticipantOptions ? (
            <div className={pageStyles.createContactsLoading}>{t('loadingParticipantOptions', { ns: 'conversations' })}</div>
          ) : (
            <div className={pageStyles.createContactsList}>
              {filteredParticipantOptions.map(option => (
                <label key={option.selectionKey} className={pageStyles.createContactItem}>
                  <input
                    type={createConversationType === 'channel' ? 'checkbox' : 'radio'}
                    name={createConversationType === 'channel' ? undefined : 'create-conversation-contact'}
                    checked={createConversationType === 'channel' ? selectedParticipantIds.has(option.selectionKey) : selectedParticipantId === option.selectionKey}
                    onChange={() => {
                      if (createConversationType === 'channel') {
                        toggleChannelParticipantSelection(option.selectionKey);
                      } else {
                        toggleParticipantSelection(option.selectionKey);
                      }
                    }}
                  />
                  <span className={pageStyles.createContactText}>
                    <span className={pageStyles.createContactTitle}>
                      <span>{getParticipantDisplayTitle(option)}</span>
                      {createConversationType === 'external-chat' && option.contactChannelType && (
                        <ConversationChannelBadge
                          icon={{ key: option.contactChannelType, kind: option.contactChannelType as any, label: getContactChannelTypeLabel(option.contactChannelType) }}
                          compact
                          disableTooltip
                        />
                      )}
                    </span>
                    <span className={pageStyles.createContactMeta}>{getParticipantMeta(option)}</span>
                  </span>
                </label>
              ))}
              {participantOptions.length === 0 && (
                <div className={pageStyles.createContactsEmpty}>{t('noParticipantsAvailable', { ns: 'conversations' })}</div>
              )}
              {participantOptions.length > 0 && filteredParticipantOptions.length === 0 && (
                <div className={pageStyles.createContactsEmpty}>
                  {createConversationType === 'external-chat'
                    ? t('noExternalRecipientsMatchSearch', { ns: 'conversations' })
                    : t('noParticipantsMatchSearch', { ns: 'conversations' })}
                </div>
              )}
            </div>
          )}
          {createConversationType === 'external-chat' && requiresChannelAccountSelection ? (
            <div className={pageStyles.createChannelAccountField}>
              <div className={pageStyles.createContactsLabel}>{t('sendFromChannelAccount', { ns: 'conversations' })}</div>
              {loadingChannelAccountsForCreate ? (
                <div className={pageStyles.createContactsLoading}>{t('loadingChannelAccounts', { ns: 'conversations' })}</div>
              ) : channelAccountsForCreate.length === 0 ? (
                <div className={pageStyles.createContactsEmpty}>{t('noChannelAccountsConfigured', { ns: 'conversations' })}</div>
              ) : (
                <select
                  className={pageStyles.createTitleInput}
                  value={selectedChannelAccountId ?? ''}
                  onChange={event => setSelectedChannelAccountId(event.target.value || null)}
                >
                  {channelAccountsForCreate.map(account => (
                    <option key={account.id} value={account.id}>
                      {formatChannelAccountOptionLabel({
                        channel: account.channel ?? 'WhatsApp',
                        displayName: account.displayName,
                        externalAccountId: account.externalAccountId,
                        connectionKind: account.connectionKind,
                      })}
                      {account.isDefault ? ` (${t('channelAccountDefault', { ns: 'companies' })})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : null}
          <div className={pageStyles.createActions}>
            <button className={pageStyles.createCancelBtn} onClick={onCancelCreatePanel} disabled={creating}>
              {t('cancel', { ns: 'conversations' })}
            </button>
            <button
              className={pageStyles.createConfirmBtn}
              onClick={onCreate}
              disabled={
                creating
                || (createConversationType === 'channel' ? selectedParticipantIds.size < 2 : !selectedParticipantId)
                || (requiresChannelAccountSelection && (!selectedChannelAccountId || loadingChannelAccountsForCreate || channelAccountsForCreate.length === 0))
              }
            >
              {creating ? t('creating', { ns: 'conversations' }) : t('create', { ns: 'conversations' })}
            </button>
          </div>
        </div>
      )}
      {isConvSection && (
        <div className={pageStyles.filterBar} ref={filterMenuRef}>
          <div className={pageStyles.filterBarTop}>
            <button
              type="button"
              className={`${pageStyles.filterTriggerBtn} ${activeFilterCount > 0 ? pageStyles.filterTriggerBtnActive : ''}`}
              onClick={() => setIsFilterMenuOpen(current => !current)}
              aria-haspopup="dialog"
              aria-expanded={isFilterMenuOpen}
              aria-label={t('filters', { ns: 'conversations' })}
              title={t('filters', { ns: 'conversations' })}
            >
              <svg viewBox="0 0 20 20" className={pageStyles.filterTriggerIcon} aria-hidden="true">
                <path d="M3.75 5.25h12.5L12 10.3v4.1l-4 1.85V10.3L3.75 5.25Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className={pageStyles.filterTriggerLabel}>{t('filters', { ns: 'conversations' })}</span>
              {activeFilterCount > 0 && <span className={pageStyles.filterTriggerCount}>{activeFilterCount}</span>}
            </button>

            <label
              className={`${pageStyles.showCommentsToggle} ${showCommentThreads ? pageStyles.showCommentsToggleActive : ''}`}
              title={t('showCommentThreadsHint', { ns: 'conversations' })}
            >
              <input
                type="checkbox"
                className={pageStyles.showCommentsInput}
                checked={showCommentThreads}
                onChange={event => setShowCommentThreads(event.target.checked)}
              />
              <span className={pageStyles.showCommentsToggleControl} aria-hidden="true">
                <span className={pageStyles.showCommentsToggleTrack}>
                  <span className={pageStyles.showCommentsToggleThumb} />
                </span>
                <svg viewBox="0 0 20 20" className={pageStyles.showCommentsToggleIcon} aria-hidden="true">
                  <path d="M10 2.75a5.25 5.25 0 0 0-4.05 8.64L5.5 17.25l4.35-1.48A5.25 5.25 0 1 0 10 2.75Z" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinejoin="round" />
                </svg>
              </span>
              <span className={pageStyles.showCommentsToggleLabel}>{t('showCommentThreads', { ns: 'conversations' })}</span>
            </label>
          </div>
          {isFilterMenuOpen ? (
            <>
              <button
                type="button"
                className={pageStyles.filterBackdrop}
                aria-label={t('close', { ns: 'common' })}
                onClick={() => setIsFilterMenuOpen(false)}
              />
              <div
                className={`${pageStyles.filterPanel} ${isMobileLayout ? pageStyles.filterPanelMobile : ''}`}
                role="dialog"
                aria-label={t('filters', { ns: 'conversations' })}
              >
                <div className={pageStyles.filterPanelHeader}>
                  <div className={pageStyles.filterPanelHeaderText}>
                    <span className={pageStyles.filterPanelTitle}>{t('filters', { ns: 'conversations' })}</span>
                    {activeFilterCount > 0 ? (
                      <span className={pageStyles.filterPanelSubtitle}>
                        {t('activeFiltersCount', { count: activeFilterCount, ns: 'conversations' })}
                      </span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={pageStyles.filterPanelCloseBtn}
                    onClick={() => setIsFilterMenuOpen(false)}
                    aria-label={t('close', { ns: 'common' })}
                  >
                    ×
                  </button>
                </div>

                <div className={pageStyles.filterPanelBody}>
                  <div className={pageStyles.filterSection}>
                    <span className={pageStyles.filterSectionTitle}>{t('customListsSection', { ns: 'conversations' })}</span>
                    <div className={pageStyles.filterChipGroup}>
                      <button
                        type="button"
                        className={`${pageStyles.filterChip} ${!activeCustomListId && navSection !== 'spam' ? pageStyles.filterChipActive : ''}`}
                        onClick={handleSelectInboxView}
                      >
                        {t('allInbox', { ns: 'conversations' })}
                      </button>
                      {customLists.map(list => (
                        <div key={list.id} className={pageStyles.filterChipWrap}>
                          <button
                            type="button"
                            className={`${pageStyles.filterChip} ${activeCustomListId === list.id ? pageStyles.filterChipActive : ''}`}
                            onClick={() => handleSelectCustomList(list.id)}
                            title={list.name}
                          >
                            <span className={pageStyles.filterChipLabel}>{list.name}</span>
                            {list.conversationCount > 0 ? (
                              <span className={pageStyles.filterChipCount}>{list.conversationCount}</span>
                            ) : null}
                          </button>
                          <button
                            type="button"
                            className={`${pageStyles.filterChipEditBtn} ${editingCustomListId === list.id ? pageStyles.filterChipEditBtnActive : ''}`}
                            onClick={() => startEditCustomList(list)}
                            aria-label={t('editCustomList', { name: list.name, ns: 'conversations' })}
                            title={t('editCustomList', { name: list.name, ns: 'conversations' })}
                          >
                            <svg viewBox="0 0 20 20" className={pageStyles.filterChipEditIcon} aria-hidden="true">
                              <path d="M12.4 3.6 16.4 7.6M4.8 15.2l-1.1 3.3 6.6-1.9 8.7-8.7a1.6 1.6 0 0 0 0-2.3l-1.9-1.9a1.6 1.6 0 0 0-2.3 0l-8.7 8.7Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className={`${pageStyles.filterChip} ${pageStyles.filterChipAdd}`}
                        onClick={handleCreateCustomListFromFilter}
                        title={t('createCustomList', { ns: 'conversations' })}
                      >
                        + {t('createCustomList', { ns: 'conversations' })}
                      </button>
                    </div>
                    {editingCustomListId ? (
                      <div className={pageStyles.filterCustomListEditor}>
                        <label className={pageStyles.filterCustomListEditorLabel} htmlFor="custom-list-name-editor">
                          {t('editCustomListName', { ns: 'conversations' })}
                        </label>
                        <input
                          id="custom-list-name-editor"
                          className={pageStyles.filterCustomListInput}
                          value={editCustomListName}
                          onChange={event => setEditCustomListName(event.target.value)}
                          maxLength={120}
                          disabled={customListMutationBusy}
                        />
                        <div className={pageStyles.filterCustomListEditorActions}>
                          <button
                            type="button"
                            className={pageStyles.filterCustomListSaveBtn}
                            onClick={() => void handleSaveCustomListEdit()}
                            disabled={customListMutationBusy || !editCustomListName.trim()}
                          >
                            {customListMutationBusy ? t('saving', { ns: 'common' }) : t('save', { ns: 'common' })}
                          </button>
                          <button
                            type="button"
                            className={pageStyles.filterCustomListCancelBtn}
                            onClick={cancelEditCustomList}
                            disabled={customListMutationBusy}
                          >
                            {t('cancel', { ns: 'common' })}
                          </button>
                          <button
                            type="button"
                            className={pageStyles.filterCustomListDeleteBtn}
                            onClick={() => void handleDeleteCustomList()}
                            disabled={customListMutationBusy}
                          >
                            {t('deleteCustomList', { ns: 'conversations' })}
                          </button>
                        </div>
                      </div>
                    ) : activeCustomList ? (
                      <p className={pageStyles.filterSectionHint}>{t('customListFilterHint', { name: activeCustomList.name, ns: 'conversations' })}</p>
                    ) : (
                      <p className={pageStyles.filterSectionHint}>{t('customListsSectionHint', { ns: 'conversations' })}</p>
                    )}
                  </div>

                  <div className={pageStyles.filterPanelGrid}>
                    {navSection !== 'channels' && (
                      <>
                        <div className={pageStyles.filterSection}>
                          <span className={pageStyles.filterSectionTitle}>{t('status', { ns: 'conversations' })}</span>
                          <div className={pageStyles.filterChipGroup}>
                            {statusFilterOptions.map(option => (
                              <button
                                key={option.value}
                                type="button"
                                className={`${pageStyles.filterChip} ${filterStatus === option.value ? pageStyles.filterChipActive : ''}`}
                                onClick={() => handleStatusCheckboxChange(option.value)}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className={pageStyles.filterSection}>
                          <span className={pageStyles.filterSectionTitle}>{t('aiAssistant', { ns: 'conversations' })}</span>
                          <div className={pageStyles.filterChipGroup}>
                            {aiFilterOptions.map(option => (
                              <button
                                key={option.value}
                                type="button"
                                className={`${pageStyles.filterChip} ${filterAiMuted === option.value ? pageStyles.filterChipActive : ''}`}
                                onClick={() => handleAiCheckboxChange(option.value)}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className={pageStyles.filterSection}>
                    <span className={pageStyles.filterSectionTitle}>{t('channel', { ns: 'conversations' })}</span>
                    <div className={pageStyles.filterChipGroup}>
                      {channelFilterOptions.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          className={`${pageStyles.filterChip} ${filterChannel === option.value ? pageStyles.filterChipActive : ''}`}
                          onClick={() => handleChannelCheckboxChange(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={pageStyles.filterSection}>
                    <span className={pageStyles.filterSectionTitle}>{t('filterByDate', { ns: 'conversations' })}</span>
                    <LocalizedDateInput
                      className={pageStyles.filterDate}
                      displayFormat="medium"
                      fillWidth
                      value={filterDate}
                      onValueChange={setFilterDate}
                      emptyLabel={t('selectDate', { ns: 'calendar' })}
                    />
                  </div>
                </div>

                <div className={pageStyles.filterActions}>
                  <button
                    type="button"
                    className={pageStyles.filterClearBtn}
                    onClick={handleClearFilters}
                    disabled={activeFilterCount === 0}
                  >
                    {t('clearAllFilters', { ns: 'conversations' })}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}
      {isConvSection && (
      <div className={pageStyles.searchBox}>
        <input
          ref={searchInputRef}
          className={pageStyles.searchInput}
          placeholder={t('searchConversationPlaceholder', { ns: 'conversations' })}
          value={search}
          onChange={event => setSearch(event.target.value)}
          onFocus={() => {
            if (globalSearchResults.length > 0) {
              setShowSearchPanel(true);
            }
          }}
          onBlur={() => window.setTimeout(() => setShowSearchPanel(false), 200)}
        />
        {showSearchPanel && (
          <div className={pageStyles.globalSearchPanel}>
            <div className={pageStyles.globalSearchHeader}>{t('messageSearchResults', { ns: 'conversations' })}</div>
            {globalSearchResults.length === 0 ? (
              <div className={pageStyles.globalSearchEmpty}>{t('noMessagesFound', { ns: 'conversations' })}</div>
            ) : (
              <ul className={pageStyles.globalSearchList}>
                {globalSearchResults.map((result, index) => (
                  <li
                    key={`${result.message.id}-${result.conversationId}-${index}`}
                    className={pageStyles.globalSearchItem}
                    onMouseDown={() => onSelectSearchResult(result)}
                  >
                    <div className={pageStyles.globalSearchMsgContent}>
                      <span className={pageStyles.globalSearchMsgText}>{result.message.content}</span>
                      <span className={pageStyles.globalSearchMsgMeta}>
                        {t('inConversation', { ns: 'conversations' })} <b>{displayConversationTitle(result.conversationTitle)}</b> • {formatDateTime(result.message.timestamp)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      )}
      {isConvSection && (
        loadingList ? (
          <div style={{ padding: 12 }}><Spinner text={t('loading', { ns: 'common' })} /></div>
        ) : listError ? (
          <div className={pageStyles.empty}>{listError}</div>
        ) : (
          <div className={pageStyles.listScroll} ref={listScrollRef} onClick={() => setOpenRowMenuId(null)}>
            {orderedFilteredList.length === 0 && (
              <div className={pageStyles.empty}>{activeCustomListId ? t('noCustomListConversationsFound', { ns: 'conversations' }) : navSection === 'direct-messages' ? t('noDirectMessagesFound', { ns: 'conversations' }) : navSection === 'channels' ? t('noChannelsFound', { ns: 'conversations' }) : navSection === 'external' ? t('noExternalConversationsFound', { ns: 'conversations' }) : navSection === 'spam' ? t('noSpamConversationsFound', { ns: 'conversations' }) : t('noConversationsFound', { ns: 'conversations' })}</div>
            )}
            {orderedFilteredList.map(conversation => {
              const conversationKind = getConversationKind(conversation);
              const hasExternalParticipant = isExternalConversation(conversation);
              const isUnsigned = hasExternalParticipant && !conversation.isSigned;
              const isFavorite = !!conversation.isFavorite;
              const isPinned = !!conversation.isPinned;
              const hasNote = !!(conversation.note && conversation.note.trim());
              const unreadCount = unreadCountByConversationId[conversation.id] ?? 0;
              const hasUnread = unreadCount > 0;
              const assigneeName = conversation.assignedToEmployeeName?.trim();
              const assignSearchQuery = assignEmployeeSearch.trim().toLocaleLowerCase();
              const filteredAssignEmployees = openRowMenuId === conversation.id
                ? employeeOptions.filter((employee) => (
                  !assignSearchQuery || employee.name.toLocaleLowerCase().includes(assignSearchQuery)
                ))
                : [];
              const preview = getConversationPreview(conversation);
              const listTimestamp = formatConversationListTimestamp(getConversationLastActivityIso(conversation));
              const listTimestampTitle = formatDateTime(getConversationLastActivityIso(conversation));
              const conversationChannelIcons = getConversationRowChannelIcons(conversation, t);
              const conversationChannelIconTitles = getConversationChannelIconTitles(conversation, t, { maskChannelValues });
              const convType: 'external' | 'dm' | 'channel' | 'other' = conversationKind === 'internal-direct'
                ? 'dm'
                : conversationKind === 'internal-channel'
                  ? 'channel'
                  : conversationKind === 'external-direct' || conversationKind === 'external-channel'
                    ? 'external'
                    : 'other';
              const directParticipant = getDirectConversationParticipant(conversation);
              const directParticipantOption = directParticipant
                ? participantOptionByKey.get(`${directParticipant.type}:${directParticipant.id}`)
                : undefined;
              const directAvatarTitle = directParticipant ? getConversationDisplayName(conversation) : '';
              const directAvatarUrl = directParticipantOption?.avatarUrl;
              const directAvatarColor = directParticipantOption?.avatarColor;
              const displayName = getConversationDisplayName(conversation);
              const avatarInitials = resolveNameInitials(directParticipant ? directAvatarTitle : displayName);
              const isInstagramCommentThread = showCommentThreads && resolveConversationHasInstagramComments(conversation);
              const isMessengerCommentThread = showCommentThreads && resolveConversationHasMessengerComments(conversation);

              const primaryChannelIcon = conversationChannelIcons[0];
              const externalChannelIcon = convType === 'external'
                ? resolveExternalConversationAvatarIcon(conversation, t, directParticipant ?? null)
                : null;
              // Letting the channel tile stand in for the avatar hides who the row is about, so it is
              // reserved for contacts that have neither a photo nor a name to draw initials from.
              const fallbackChannelAvatarIcon = directAvatarUrl || avatarInitials ? null : externalChannelIcon;
              const showChannelIconAsAvatar = Boolean(fallbackChannelAvatarIcon);
              // Phone, SMS and TikTok are not "social" icons, so relying on the social-only list left
              // those rows with no channel marker at all. Any external channel earns the badge, but a
              // bare customer or lead icon says nothing about the channel and would only add noise.
              const overlayChannelIcon = externalChannelIcon && externalChannelIcon.kind !== 'customer' && externalChannelIcon.kind !== 'lead'
                ? externalChannelIcon
                : primaryChannelIcon;
              const overlayChannelTitle = overlayChannelIcon
                ? (conversationChannelIconTitles.get(overlayChannelIcon.key) ?? overlayChannelIcon.label)
                : undefined;
              const showChannelOverlayBadge = Boolean(overlayChannelIcon && !showChannelIconAsAvatar);

              return (
                <div
                  key={conversation.id}
                  className={`${pageStyles.row} ${selectedId === conversation.id ? pageStyles.active : ''} ${hasUnread ? pageStyles.rowUnread : ''} ${isUnsigned ? pageStyles.unsigned : ''} ${convType === 'external' ? pageStyles.rowExternal : convType === 'dm' ? pageStyles.rowDm : convType === 'channel' ? pageStyles.rowChannel : ''}`}
                >
                  <div onClick={() => onSelectConversation(conversation.id)} className={pageStyles.rowMain}>
                    <div className={pageStyles.rowContent}>
                      <div className={pageStyles.rowAvatarWrap}>
                        {directParticipant ? (
                          showChannelIconAsAvatar && fallbackChannelAvatarIcon ? (
                            <ConversationChannelBadge
                              icon={fallbackChannelAvatarIcon}
                              title={overlayChannelTitle ?? fallbackChannelAvatarIcon.label}
                              size="avatar"
                              disableTooltip
                            />
                          ) : (
                          <span
                            className={`${pageStyles.rowAvatar} ${hasUnread ? pageStyles.rowAvatarUnread : ''}`}
                            title={directAvatarTitle}
                            style={directAvatarColor ? { backgroundColor: directAvatarColor } : undefined}
                            aria-hidden="true"
                          >
                            {directAvatarUrl ? (
                              <img src={directAvatarUrl} alt="" className={pageStyles.rowAvatarImage} loading="lazy" />
                            ) : (
                              <span className={pageStyles.rowAvatarFallback}>{avatarInitials ?? '?'}</span>
                            )}
                          </span>
                          )
                        ) : convType === 'channel' ? (
                          <span className={`${pageStyles.rowAvatar} ${pageStyles.rowAvatarChannel} ${hasUnread ? pageStyles.rowAvatarUnread : ''}`} aria-hidden="true">
                            <span className={pageStyles.rowAvatarChannelMark}>#</span>
                          </span>
                        ) : showChannelIconAsAvatar && fallbackChannelAvatarIcon ? (
                          <ConversationChannelBadge
                            icon={fallbackChannelAvatarIcon}
                            title={overlayChannelTitle ?? fallbackChannelAvatarIcon.label}
                            size="avatar"
                            disableTooltip
                          />
                        ) : (
                          <span
                            className={`${pageStyles.rowAvatar} ${convType === 'external' ? pageStyles.rowAvatarExternal : ''} ${hasUnread ? pageStyles.rowAvatarUnread : ''}`}
                            aria-hidden="true"
                          >
                            <span className={pageStyles.rowAvatarFallback}>{avatarInitials ?? '?'}</span>
                          </span>
                        )}
                        {showChannelOverlayBadge && overlayChannelIcon && (
                          <span className={pageStyles.rowAvatarChannelBadge} title={overlayChannelTitle}>
                            <ConversationChannelBadge
                              icon={overlayChannelIcon}
                              title={overlayChannelTitle}
                              size="overlay"
                              disableTooltip
                            />
                          </span>
                        )}
                      </div>

                      <div className={pageStyles.rowTextBlock}>
                        <div className={pageStyles.rowHeader}>
                          <span className={pageStyles.rowTitle}>{displayName}</span>
                          {isMessengerCommentThread ? (
                            <span className={`${pageStyles.rowThreadPill} ${pageStyles.rowThreadPillMessenger}`} title={t('messengerCommentThreadLabel', { ns: 'conversations' })}>
                              {t('messengerCommentThreadShortLabel', { ns: 'conversations' })}
                            </span>
                          ) : null}
                          {isInstagramCommentThread ? (
                            <span className={pageStyles.rowThreadPill} title={t('instagramCommentThreadLabel', { ns: 'conversations' })}>
                              {t('instagramCommentThreadShortLabel', { ns: 'conversations' })}
                            </span>
                          ) : null}
                          <div className={pageStyles.rowHeaderMeta}>
                            {(isPinned || isFavorite || hasNote) && (
                              <span className={pageStyles.rowIndicators} aria-hidden="true">
                                {isPinned && <span className={pageStyles.rowIndicator}>📌</span>}
                                {isFavorite && <span className={`${pageStyles.rowIndicator} ${pageStyles.rowIndicatorFavorite}`}>★</span>}
                                {hasNote && <span className={pageStyles.rowIndicator}>📝</span>}
                              </span>
                            )}
                            <span className={pageStyles.rowTimestamp} title={listTimestampTitle}>{listTimestamp}</span>
                            {hasUnread && (
                              <span className={pageStyles.unreadBadge}>
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className={`${pageStyles.rowPreview} ${hasUnread ? pageStyles.rowPreviewUnread : ''}`} title={preview}>
                          {preview}
                        </div>

                        {assigneeName ? (
                          <div className={pageStyles.rowAssignee}>
                            <span className={pageStyles.rowAssigneeLabel}>{t('assignee', { ns: 'conversations' })}</span>
                            <span className={pageStyles.rowAssigneeName}>{assigneeName}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className={pageStyles.rowActions}>
                    <button
                      type="button"
                      className={`${pageStyles.rowActionBtn} ${pageStyles.floatingChatBtn}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenFloatingChat(conversation.id);
                        setOpenRowMenuId(null);
                      }}
                      title={t('openInFloatingChat', { ns: 'conversations' })}
                      aria-label={t('openInFloatingChat', { ns: 'conversations' })}
                    >
                      <svg className={pageStyles.rowActionIcon} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M4 4.5h12a1.5 1.5 0 0 1 1.5 1.5v5.5A1.5 1.5 0 0 1 16 12H9l-3.5 3v-3H4A1.5 1.5 0 0 1 2.5 10.5V6A1.5 1.5 0 0 1 4 4.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <div
                      ref={openRowMenuId === conversation.id ? rowMenuWrapRef : undefined}
                      className={`${pageStyles.rowMenuWrap} ${openRowMenuId === conversation.id ? pageStyles.rowMenuWrapOpen : ''}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        className={`${pageStyles.rowActionBtn} ${pageStyles.rowMenuBtn} ${openRowMenuId === conversation.id ? pageStyles.rowMenuBtnOpen : ''}`}
                        title={t('conversationOptions', { ns: 'conversations' })}
                        aria-expanded={openRowMenuId === conversation.id}
                        aria-haspopup="menu"
                        onClick={() => setOpenRowMenuId(prev => (prev === conversation.id ? null : conversation.id))}
                      >
                        <span className={pageStyles.rowMenuBtnIcon} aria-hidden="true">⋮</span>
                      </button>
                    {openRowMenuId === conversation.id && (
                      <div className={pageStyles.rowMenu}>
                        {navSection !== 'spam' && !activeCustomListId && (
                          <button className={pageStyles.rowMenuItem} onClick={() => togglePinnedConversation(conversation.id, isPinned)}>
                            {isPinned ? t('unpinConversation', { ns: 'conversations' }) : t('pinConversation', { ns: 'conversations' })}
                          </button>
                        )}
                        {navSection !== 'spam' && !activeCustomListId && (
                          <button className={pageStyles.rowMenuItem} onClick={() => toggleFavoriteConversation(conversation.id, isFavorite)}>
                            {isFavorite ? t('removeFromFavorites', { ns: 'conversations' }) : t('addToFavorites', { ns: 'conversations' })}
                          </button>
                        )}
                        <button className={pageStyles.rowMenuItem} onClick={() => onOpenConversationNotes(conversation.id)}>
                          {hasNote ? t('openNotes', { ns: 'conversations' }) : t('addNote', { ns: 'conversations' })}
                        </button>
                        {isExternalConversation(conversation) && navSection !== 'spam' ? (
                          <div className={pageStyles.rowMenuAssignSection}>
                            <div className={pageStyles.rowMenuSectionTitle}>
                              {t('moveToList', { ns: 'conversations' })}
                            </div>
                            <div className={pageStyles.rowMenuAssignList}>
                              {customLists.length === 0 ? (
                                <div className={pageStyles.rowMenuAssignEmpty}>
                                  {t('noCustomListsFound', { ns: 'conversations' })}
                                </div>
                              ) : customLists.map((list) => {
                                const isCurrentList = conversation.customListId === list.id;

                                return (
                                  <button
                                    key={list.id}
                                    type="button"
                                    className={`${pageStyles.rowMenuItem} ${isCurrentList ? pageStyles.rowMenuItemActive : ''}`}
                                    onClick={() => onMoveConversationToCustomList(conversation.id, list.id)}
                                    disabled={isCurrentList}
                                  >
                                    {list.name}
                                  </button>
                                );
                              })}
                              {conversation.customListId ? (
                                <button
                                  type="button"
                                  className={pageStyles.rowMenuItem}
                                  onClick={() => onMoveConversationToCustomList(conversation.id, null)}
                                >
                                  {t('removeFromCustomList', { ns: 'conversations' })}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                        {navSection === 'spam' ? (
                          <button className={pageStyles.rowMenuItem} onClick={() => restoreConversationForMe(conversation.id)}>
                            {t('removeFromSpam', { ns: 'conversations' })}
                          </button>
                        ) : (
                          <button className={`${pageStyles.rowMenuItem} ${pageStyles.rowMenuItemDanger}`} onClick={() => hideConversationForMe(conversation.id)}>
                            {t('markAsSpam', { ns: 'conversations' })}
                          </button>
                        )}
                        {employeeOptions.length > 0 ? (
                          <div className={pageStyles.rowMenuAssignSection}>
                            <div className={pageStyles.rowMenuSectionTitle}>
                              {t('assignConversation', { ns: 'conversations' })}
                            </div>
                            <input
                              type="search"
                              className={pageStyles.rowMenuAssignSearch}
                              value={assignEmployeeSearch}
                              onChange={(event) => setAssignEmployeeSearch(event.target.value)}
                              placeholder={t('searchEmployeesToAssign', { ns: 'conversations' })}
                              aria-label={t('searchEmployeesToAssign', { ns: 'conversations' })}
                            />
                            <div className={pageStyles.rowMenuAssignList}>
                              {filteredAssignEmployees.length === 0 ? (
                                <div className={pageStyles.rowMenuAssignEmpty}>
                                  {t('noEmployeesFound', { ns: 'conversations' })}
                                </div>
                              ) : filteredAssignEmployees.map((employee) => {
                                const isCurrentAssignee = conversation.assignedToEmployeeId === employee.id;

                                return (
                                  <button
                                    key={employee.id}
                                    type="button"
                                    className={`${pageStyles.rowMenuItem} ${isCurrentAssignee ? pageStyles.rowMenuItemActive : ''}`}
                                    onClick={() => onAssignConversation(conversation.id, employee.id)}
                                    disabled={isCurrentAssignee}
                                  >
                                    <span>{employee.name}</span>
                                    {isCurrentAssignee ? <span className={pageStyles.rowMenuItemCheck} aria-hidden="true">✓</span> : null}
                                  </button>
                                );
                              })}
                            </div>
                            {assigneeName ? (
                              <button className={pageStyles.rowMenuItem} onClick={() => onUnassignConversation(conversation.id)}>
                                {t('unassign', { ns: 'conversations' })}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    )}
                    </div>
                  </div>
                </div>
              );
            })}
            {hasMoreConversations ? (
              <div ref={loadMoreSentinelRef} className={pageStyles.listLoadMoreSentinel} aria-hidden="true" />
            ) : null}
            {loadingMoreList ? (
              <div className={pageStyles.listLoadMore}>
                <Spinner text={t('loadingMoreConversations', { ns: 'conversations' })} />
              </div>
            ) : null}
          </div>
        )
      )}

      {navSection === 'drafts-sent' && (
        <div className={pageStyles.listScroll}>
          {(loadingScheduledNav && draftsSentTab === 'scheduled') && (
            <div style={{ padding: 12 }}><Spinner text={t('loadingScheduledMessages', { ns: 'conversations' })} /></div>
          )}

          {draftsSentTab === 'scheduled' && !loadingScheduledNav && scheduledNavItems.length === 0 && (
            <div className={pageStyles.empty}>{t('noScheduledMessages', { ns: 'conversations' })}</div>
          )}

          {draftsSentTab === 'scheduled' && scheduledNavItems.map(item => (
            <button
              key={item.id}
              type="button"
              className={pageStyles.messageNavRow}
              onClick={() => onSelectScheduledNavItem(item.conversationId)}
            >
              <div className={pageStyles.messageNavHeader}>
                <span className={pageStyles.messageNavConversation}>{displayConversationTitle(item.conversationTitle)}</span>
                <span className={pageStyles.messageNavTime}>{formatDateTime(item.scheduledAtUtc)}</span>
              </div>
              <div className={pageStyles.messageNavContent}>{formatNavMessagePreview(item.content, item.messageType, item.fileName)}</div>
            </button>
          ))}

          {draftsSentTab === 'sent' && sentNavItems.length === 0 && (
            <div className={pageStyles.empty}>{t('noSentMessages', { ns: 'conversations' })}</div>
          )}

          {draftsSentTab === 'sent' && sentNavItems.map(item => (
            <button
              key={item.id}
              type="button"
              className={pageStyles.messageNavRow}
              onClick={() => onSelectSentNavItem(item.conversationId, item.id)}
            >
              <div className={pageStyles.messageNavHeader}>
                <span className={pageStyles.messageNavConversation}>{displayConversationTitle(item.conversationTitle)}</span>
                <span className={pageStyles.messageNavTime}>{formatDateTime(item.timestamp)}</span>
              </div>
              <div className={pageStyles.messageNavContent}>{formatNavMessagePreview(item.content, item.messageType, item.fileName)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export { ConversationListPane };
export default ConversationListPane;
